import { Link } from 'react-router'
import { Button } from '@/components/ui/button'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle
} from '@/components/ui/empty'
import { Lock03Icon } from '@/components/ui/icons'
import { useTranslation } from '@/translations/i18n'

/**
 * What a screen shows in place of itself when the operator may never perform
 * the one operation it exists for (FR51): the create and edit forms.
 *
 * No button in the app leads here, since every creator-only affordance is
 * hidden rather than disabled. This is what a URL reaches: a bookmark, a link
 * pasted by a creator, a tab left open through a role change. Saying so beats
 * rendering the form, which would take the operator through the whole thing to
 * a 403 on submit.
 *
 * Deliberately the same shape as the edit form's "this event is locked" screen,
 * because they answer the same question at the same moment. They are separate
 * checks though: one is about the operator, the other about the event, and
 * neither implies the other.
 */
function CreatorOnlyNotice({
	backLabel,
	backTo,
	title,
	description
}: {
	backLabel: string
	backTo: string
	/**
	 * For a screen gated on something other than the creator role (the admin
	 * Team screen). Defaults to the creator wording.
	 */
	title?: string
	/**
	 * Which creator-only screen this is, for the screens whose operation is not
	 * event setup. Defaults to the setup wording the create and edit forms use.
	 */
	description?: string
}) {
	const { t } = useTranslation()

	return (
		<Empty className='flex-1 py-24'>
			<EmptyHeader>
				<EmptyMedia variant='icon'>
					<Lock03Icon />
				</EmptyMedia>
				<EmptyTitle>
					{title ?? t('marquee:creator_only_title')}
				</EmptyTitle>
				<EmptyDescription>
					{description ?? t('marquee:creator_only_description')}
				</EmptyDescription>
			</EmptyHeader>
			<Button asChild size='sm' variant='secondaryOutline'>
				<Link to={backTo}>{backLabel}</Link>
			</Button>
		</Empty>
	)
}

export { CreatorOnlyNotice }
